/**
 * Atmosphere — wispy cloud sprites + festival flags scattered through the
 * world. Pure visual flair, no logic.
 */

import * as THREE from 'three';
import type { Terrain } from '@/world/Terrain';
import { mulberry32 } from '@/core/MathUtils';

const CLOUD_PALETTE = ['#ffffff', '#fff5e8', '#ffeed4'];

const FLAG_COLORS = [
  '#ff5f6d', '#ffc371', '#7ce0a3', '#56c2ff',
  '#bd72ff', '#ff8ec0', '#f5f0e8', '#ffaa3c'
];

export class Atmosphere {
  static buildClouds(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Clouds';

    // Use canvas-generated soft sprites for clouds
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(128, 128, 30, 128, 128, 128);
    grad.addColorStop(0, 'rgba(255,255,255,0.85)');
    grad.addColorStop(0.5, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0.0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 256);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;

    const rand = mulberry32(31337);
    const cloudCount = 24;
    for (let i = 0; i < cloudCount; i++) {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: CLOUD_PALETTE[Math.floor(rand() * CLOUD_PALETTE.length)],
        transparent: true,
        depthWrite: false,
        opacity: 0.55 + rand() * 0.35,
        fog: false
      });
      const sprite = new THREE.Sprite(mat);
      // Distribute across a wide cylinder shell at ~400m altitude
      const angle = rand() * Math.PI * 2;
      const dist = 600 + rand() * 800;
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      const y = 250 + rand() * 220;
      sprite.position.set(x, y, z);
      const scale = 200 + rand() * 320;
      sprite.scale.set(scale, scale * 0.55, 1);
      group.add(sprite);
    }
    return group;
  }

  /**
   * Festival flags & totems — tall poles with colorful banner sprites.
   * Distributed near walking paths and the audience areas.
   */
  static buildFlags(
    terrain: Terrain,
    centers: { x: number; z: number; r: number; count: number }[]
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Flags';

    const matPole = new THREE.MeshStandardMaterial({
      color: '#444',
      metalness: 0.7,
      roughness: 0.5
    });

    const rand = mulberry32(2025);

    for (const center of centers) {
      let placed = 0;
      let attempts = 0;
      while (placed < center.count && attempts < center.count * 6) {
        attempts++;
        const a = rand() * Math.PI * 2;
        const r = Math.sqrt(rand()) * center.r;
        const x = center.x + Math.cos(a) * r;
        const z = center.z + Math.sin(a) * r;
        const y = terrain.heightAt(x, z);

        const flag = new THREE.Group();
        flag.position.set(x, y, z);

        const poleH = 5 + rand() * 3.5;
        const pole = new THREE.Mesh(
          new THREE.CylinderGeometry(0.06, 0.06, poleH, 6),
          matPole
        );
        pole.position.y = poleH / 2;
        flag.add(pole);

        // Banner — bright and emissive so it pops at any distance
        const c1 = FLAG_COLORS[Math.floor(rand() * FLAG_COLORS.length)];
        const bannerMat = new THREE.MeshStandardMaterial({
          color: c1,
          emissive: c1,
          emissiveIntensity: 1.6,
          side: THREE.DoubleSide,
          roughness: 0.85
        });
        const banner = new THREE.Mesh(
          new THREE.PlaneGeometry(1.6, 2.2),
          bannerMat
        );
        banner.position.set(0.85, poleH - 1.3, 0);
        flag.add(banner);

        // Tip ornament — glowing globe
        const tipColor = FLAG_COLORS[Math.floor(rand() * FLAG_COLORS.length)];
        const tipMat = new THREE.MeshStandardMaterial({
          color: tipColor,
          emissive: tipColor,
          emissiveIntensity: 2.2
        });
        const tip = new THREE.Mesh(
          new THREE.SphereGeometry(0.18, 8, 6),
          tipMat
        );
        tip.position.y = poleH + 0.1;
        flag.add(tip);

        flag.rotation.y = rand() * Math.PI * 2;
        group.add(flag);
        placed++;
      }
    }

    return group;
  }

  /**
   * Subtle dust haze planes near the ground around audience zones.
   */
  static buildHaze(centers: { x: number; z: number; r: number }[]): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Haze';
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(128, 128, 20, 128, 128, 128);
    grad.addColorStop(0, 'rgba(255,220,180,0.45)');
    grad.addColorStop(0.6, 'rgba(255,220,180,0.15)');
    grad.addColorStop(1, 'rgba(255,220,180,0.0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 256);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;

    for (const c of centers) {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        opacity: 0.35,
        fog: false
      });
      const sprite = new THREE.Sprite(mat);
      sprite.position.set(c.x, 8, c.z);
      sprite.scale.set(c.r * 2, c.r * 0.8, 1);
      group.add(sprite);
    }
    return group;
  }
}
